import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle } from "lucide-react";

const ABOUT_IMAGE =
  "https://images.unsplash.com/photo-1519389950473-47ba0277781c";

const highlights = [
  "Strategy before execution",
  "Custom solutions, not templates",
  "Built for growth and usability",
  "Support beyond launch",
];

export const AboutSection = () => {
  return (
    <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-accent/50 to-transparent pointer-events-none" />

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src={ABOUT_IMAGE}
                alt="About Square Trigger"
                className="w-full h-auto object-cover aspect-[4/3]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 to-transparent" />
            </div>

            {/* Decorative Element */}
            <div className="absolute -top-4 -left-4 w-20 h-20 sm:w-24 sm:h-24 border-2 border-primary/30 rounded-2xl" />
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            {/* Eyebrow */}
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              About Us
            </span>

            {/* H2 */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mt-3 mb-5">
              We Start With the Problem. Then We Build the{" "}
              <span className="gradient-text">Right Solution.</span>
            </h2>

            {/* Body */}
            <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
              Square Trigger is a digital agency in Vancouver helping businesses
              grow through website development, SEO services, social media
              marketing, custom tool creation, and ongoing support. We do not
              start with fixed packages. We start by understanding what your
              business actually needs online.
            </p>

            {/* Highlights */}
            <div className="mx-auto lg:mx-0 max-w-xl mb-9 text-center sm:text-left">
              <ul className="inline-grid sm:grid sm:grid-cols-2 gap-x-8 gap-y-3 text-left">
                {highlights.map((label, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-primary mt-[2px] shrink-0" />
                    <span className="text-sm sm:text-base text-foreground/80 leading-relaxed">
                      {label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <Button
              variant="gradient"
              size="lg"
              asChild
              className="group w-full sm:w-auto justify-center"
            >
             <Link
  to="/about"
  className="flex items-center justify-center gap-2"
>
  Learn More About Us
  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
</Link>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};